import * as _ from 'lodash';
import { useState, useCallback, useMemo } from 'react';
import { IMicrositeRecipientLog as BaseMicrositeRecipientLog } from '@nkr/models/index';

// Extended interface to include fields from the API that aren't in the base interface
interface IMicrositeRecipientLog extends BaseMicrositeRecipientLog {
    eventDate?: string;
    createdDateTime?: string;
}
import { Label, FormGroup, ControlLabel, FormControl, Row, Col, Panel, Badge, Well, Glyphicon, Collapse, Table } from 'react-bootstrap';
import { ColDef, GridApi, GridReadyEvent, IHeaderParams, ValueFormatterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { HistoryTitle } from './styles';
import { Utility } from '@nkr/services/utility';
import SortableHeaderComponent from '../admin/center/list/common/sortable-header';
import * as moment from 'moment';
import { MicrositeEventType } from '@nkr/enums/microsite-event-type';
import React from 'react';
import styled from 'styled-components';

const HistoryGridWrapper = styled.div`
    width: 100%;
    overflow: hidden;
    border: 1px solid #ddd;
    border-radius: 4px;
    margin-top: 15px;

    .list-cell {
        border-bottom: none;
        text-align: center;
        line-height: 20px;
        border-color: #ccc;
        font-size: 13px;
        display: flex;
        justify-content: center;
    }

    .list-cell > .ag-cell-wrapper {
        display: flex;
        justify-content: center;
        width: 100%;
    }

    .list-cell > .ag-cell-wrapper > .ag-cell-value,
    .list-cell > .ag-cell-wrapper > .ag-cell-value > .ag-react-container {
        width: 100%;
    }

    .list-header {
        display: flex;
        justify-content: center;
        align-items: flex-end;
        border-color: #eee;
        border-width: 0px 0px 0px 1px;
        border-style: solid;
        padding-bottom: 5px;
        background-color: #ddd;
    }

    .list-header > .ag-cell-label-container > .ag-header-cell-label {
        display: flex;
        justify-content: center;
    }
`;

const FilterContainer = styled.div`
    margin-bottom: 20px;
    background: #f9f9f9;
    padding: 15px;
    border: 1px solid #e5e5e5;
    border-radius: 4px;

    .form-group {
        margin-bottom: 0;
    }

    .control-label {
        font-weight: 600;
        margin-bottom: 5px;
    }
`;

const SummaryContainer = styled.div`
    margin-bottom: 0px;
    padding-bottom: 10px;
    // border-bottom: 1px solid #e5e5e5;
`;

const SummaryHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    
    h4 {
        margin: 0;
        font-weight: 600;
    }
    
    .toggle-btn {
        border: none;
        background: none;
        color: #337ab7;
        font-size: 12px;
        cursor: pointer;
        
        &:hover {
            text-decoration: underline;
        }
    }
`;

const SummaryCardContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
`;

const SummaryCard = styled(Panel)`
    flex: 1;
    min-width: 250px;
    
    .panel-heading {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
    }
    
    .panel-body {
        padding: 10px 15px;
    }
    
    .summary-value {
        font-size: 16px;
        font-weight: 600;
        margin: 5px 0;
    }
    
    .summary-subtext {
        font-size: 12px;
        color: #777;
    }
    
    .summary-date {
        font-weight: normal;
    }
    
    .badge {
        margin-left: 5px;
    }
`;

// Fix the StatusBadge to accept the color prop
const StatusBadge = styled(Badge)`
    background-color: ${(props: any) => props.color || '#777'};
    margin-left: 5px;
    padding: 3px 7px;
`;

// Define interfaces for summary data
interface StatusInfo {
    status: string;
    type: string;
    isActive: boolean;
    date: Date;
    timeAgo: string;
    color: string;
}

interface InvitationInfo {
    date: Date | null;
    timeAgo: string;
    count: number;
    hasInvitation: boolean;
}

interface TransplantInfo {
    hasTransplant: boolean;
    count: number;
    lastTransplantDate: Date | null;
    timeAgo: string;
    status: string;
}

interface ActivityInfo {
    totalEvents: number;
    statusUpdates: number;
    events: number;
    lastChange: {
        date: Date | null;
        description: string;
        timeAgo: string;
    };
}

interface IProps {
    name: string,
    logs: IMicrositeRecipientLog[],
    isExpanded?: boolean
}

type RecordTypeFilter = 'all' | 'event' | 'status';
type StatusTypeFilter = string;

const MicrositeHistory = (props: IProps) => {
    const [recordTypeFilter, setRecordTypeFilter] = useState<RecordTypeFilter>('all');
    const [statusTypeFilter, setStatusTypeFilter] = useState<StatusTypeFilter>('all');
    const [showSummary, setShowSummary] = useState<boolean>(true);

    // Helper function to create type label
    const getTypeLabel = (params: ValueFormatterParams) => {
        const data = params.data;
        const isEvent = data.isEvent === true;

        if (!isEvent) {
            return (
                <Label bsStyle="primary">Microsite</Label>
            );
        }

        return (
            <Label bsStyle="info">Event</Label>
        );
    };

    const formatStatusText = (status: string): string => {
        if (!status) return '';

        // Add spaces before capital letters that follow lowercase letters
        let spaced = status.replace(/([a-z])([A-Z])/g, '$1 $2');

        // Split by spaces and capitalize first letter of each word
        const words = spaced.split(' ');

        const capitalizedWords = words.map(word => {
            if (word.length === 0) return word;
            return word.charAt(0).toUpperCase() + word.slice(1);
        });

        return capitalizedWords.join(' ');
    };

    const getSpecificStatusTypes = (condensedStatus: string): string[] => {
        switch (condensedStatus.toLowerCase()) {
            case 'active': return ['Active', 'Starter Site - Active', 'Custom Site - Active'];
            case 'deactivated': return ['Deactivated', 'Other Deactivate'];
            case 'pending review': return ['Pending Coach Review', 'Pending Center Review', 'Pending NKR Review'];
            case 'approved': return ['Coach Approved', 'Center Approved', 'NKR Approved'];
            case 'declined': return ['Coach Declined', 'Center Declined', 'NKR Declined'];
            case 'changes required': return ['Changes Required', 'Cannot Be Fixed'];
            case 'medical status change': return ['Too Healthy', 'Too Sick'];
            case 'center change': return ['Moved New Center'];
            default: return [condensedStatus];
        }
    };

    const handleRecordTypeChange = (e: React.FormEvent<FormControl>) => {
        const value = (e.target as HTMLSelectElement).value as RecordTypeFilter;
        setRecordTypeFilter(value);
        setStatusTypeFilter('all');
    };

    const handleStatusTypeChange = (e: React.FormEvent<FormControl>) => {
        const value = (e.target as HTMLSelectElement).value;
        setStatusTypeFilter(value);
    };

    const isTransplantEvent = (eventType?: number): boolean => {
        return eventType === 1 || eventType === 2 || eventType === 11 || eventType === 3;
    };

    const getTransplantTypeDescription = (eventType?: number): string => {
        switch (eventType) {
            case 1: return 'Living Donor Transplant';
            case 2: return 'Deceased Donor Transplant';
            case 11: return 'Living Donor Transplant (Other Center)';
            case 3: return 'Received Voucher';
            default: return 'Unknown Transplant Type';
        }
    };

    const formatDateTime = (params: ValueFormatterParams) => {
        try {
            if (!params.value && params.data) {
                const field = params.colDef?.field;

                if (field === 'eventDate' && !params.data.eventDate) {
                    return moment(params.data.logDateTime).format('MMM D, YYYY h:mm A');
                } else if (field === 'createdDateTime' && !params.data.createdDateTime) {
                    return moment(params.data.logDateTime).format('MMM D, YYYY h:mm A');
                }
            }

            return moment(params.value).format('MMM D, YYYY h:mm A');
        } catch (e) {
            console.error('Date parsing error:', e);
            return params.value || 'N/A';
        }
    };

    const isApprovalStatus = (logEntry: string): boolean => {
        const approvalKeywords = [
            'pending approval',
            'approved',
            'declined',
            'fixable',
            'unfixable',
            'coach approval',
            'center approval',
            'nkr approval'
        ];

        return approvalKeywords.some(keyword =>
            logEntry.toLowerCase().includes(keyword)
        );
    };

    const getStatusTypeLabel = (params: ValueFormatterParams) => {
        const data = params.data;

        if (data.isEvent === true) {
            if (data.eventType !== undefined && MicrositeEventType[data.eventType]) {
                const eventType = MicrositeEventType[data.eventType];

                if (eventType === 'TooHealthy') {
                    return <Label bsStyle="info">Too Healthy</Label>;
                }

                if (eventType === 'TooSick') {
                    return <Label bsStyle="info">Too Sick</Label>;
                }

                return <Label bsStyle="info">{formatStatusText(eventType)}</Label>;
            }
            return <Label bsStyle="info">Event</Label>;
        }

        const logEntry = (data.logEntry || '').toLowerCase();
        const micrositeStatus = (data.micrositeRecipientStatus || '').toLowerCase();
        const rawStatus = data.micrositeRecipientStatus || '';

        if (micrositeStatus) {
            if (micrositeStatus.includes('active')) {
                if (micrositeStatus.includes('starter')) {
                    return <Label bsStyle="success">Starter Site - Active</Label>;
                }

                if (micrositeStatus.includes('custom')) {
                    return <Label bsStyle="success">Custom Site - Active</Label>;
                }

                return <Label bsStyle="success">Active</Label>;
            }

            if (micrositeStatus.includes('ready for coach')) {
                return <Label bsStyle="warning">Pending Coach Review</Label>;
            }

            if (micrositeStatus.includes('ready for center')) {
                return <Label bsStyle="warning">Pending Center Review</Label>;
            }

            if (micrositeStatus.includes('ready for nkr')) {
                return <Label bsStyle="warning">Pending NKR Review</Label>;
            }

            if (micrositeStatus.includes('inactive')) {
                return <Label bsStyle="default">Inactive</Label>;
            }

            if (micrositeStatus.includes('duplicate')) {
                return <Label bsStyle="warning">Duplicate Entry</Label>;
            }

            if (micrositeStatus.includes('deactivate')) {
                return <Label bsStyle="default">Deactivated</Label>;
            }

            if (micrositeStatus.includes('otherdeactivate')) {
                return <Label bsStyle="default">Other Deactivate</Label>;
            }

            if (micrositeStatus.includes('readyfortransplant')) {
                return <Label bsStyle="info">Reactivated</Label>;
            }
        }

        if (logEntry) {
            if (logEntry.includes('approved')) {
                if (logEntry.includes('coach')) {
                    return <Label bsStyle="success">Coach Approved</Label>;
                } else if (logEntry.includes('center')) {
                    return <Label bsStyle="success">Center Approved</Label>;
                } else if (logEntry.includes('nkr')) {
                    return <Label bsStyle="success">NKR Approved</Label>;
                }

                return <Label bsStyle="success">Approved</Label>;
            }

            if (logEntry.includes('declined') || logEntry.includes('denied')) {
                if (logEntry.includes('coach')) {
                    return <Label bsStyle="danger">Coach Declined</Label>;
                } else if (logEntry.includes('center')) {
                    return <Label bsStyle="danger">Center Declined</Label>;
                } else if (logEntry.includes('nkr')) {
                    return <Label bsStyle="danger">NKR Declined</Label>;
                }

                return <Label bsStyle="danger">Declined</Label>;
            }

            if (logEntry.includes('coach') || logEntry.includes('ready for coach')) {
                return <Label bsStyle="warning">Pending Coach Review</Label>;
            } else if (logEntry.includes('center') || logEntry.includes('ready for center')) {
                return <Label bsStyle="warning">Pending Center Review</Label>;
            } else if (logEntry.includes('nkr') || logEntry.includes('ready for nkr')) {
                return <Label bsStyle="warning">Pending NKR Review</Label>;
            }

            if (logEntry.includes('fixable')) {
                return <Label bsStyle="info">Changes Required</Label>;
            }

            if (logEntry.includes('unfixable')) {
                return <Label bsStyle="danger">Cannot Be Fixed</Label>;
            }

            if (logEntry.includes('inactive')) {
                return <Label bsStyle="default">Inactive</Label>;
            }

            if (logEntry.includes('active')) {
                if (logEntry.includes('starter')) {
                    return <Label bsStyle="success">Starter Site - Active</Label>;
                }
                if (logEntry.includes('custom')) {
                    return <Label bsStyle="success">Custom Site - Active</Label>;
                }
                return <Label bsStyle="success">Active</Label>;
            }

            if (logEntry.includes('pending')) {
                const details = logEntry.split(':')[1]?.trim();
                if (details) {
                    return <Label bsStyle="warning">{`Pending: ${formatStatusText(details)}`}</Label>;
                }
                return <Label bsStyle="warning">Pending Review</Label>;
            }
        }

        if (rawStatus) {
            if (rawStatus.toLowerCase().includes('active')) {
                const cleanStatus = rawStatus.replace(/^\d+\s*/, '').trim();

                if (cleanStatus) {
                    if (cleanStatus.toLowerCase().includes('starter')) {
                        return <Label bsStyle="success">Starter Site - Active</Label>;
                    }
                    if (cleanStatus.toLowerCase().includes('custom')) {
                        return <Label bsStyle="success">Custom Site - Active</Label>;
                    }
                    return <Label bsStyle="success">{formatStatusText(cleanStatus)}</Label>;
                }
            }

            const cleanStatus = rawStatus.replace(/^\d+\s*/, '').trim();

            if (cleanStatus) {
                return <Label bsStyle="default">{formatStatusText(cleanStatus)}</Label>;
            }
        }

        return <Label bsStyle="default">Status Update</Label>;
    };

    const formatDescription = (params: ValueFormatterParams) => {
        const data = params.data;
        let description = '';

        if (data.isEvent === true) {
            if (isTransplantEvent(data.eventType)) {
                const date = moment(data.logDateTime).format('MMM D, YYYY h:mm A');
                description = `${getTransplantTypeDescription(data.eventType)} - ${date}`;

                if (data.logEntry) {
                    const cleanLogEntry = data.logEntry
                        .replace('Status changed:', '')
                        .trim();
                    if (cleanLogEntry && !description.includes(cleanLogEntry)) {
                        description += ` - ${cleanLogEntry}`;
                    }
                }
            } else {
                description = data.logEntry || '';

                if (data.centerChange) {
                    description += ` (Moved from ${data.prevCenterName} to ${data.newCenterName})`;
                }

                description = description
                    .replace('Status changed:', '')
                    .replace('TooSick:', '')
                    .replace('TooHealthy:', '')
                    .replace('Too Sick for Transplant', '')
                    .replace('Too Healthy for Transplant', '')
                    .trim();

                if (data.eventType !== undefined &&
                    MicrositeEventType[data.eventType] &&
                    !description.includes('Too Sick') &&
                    !description.includes('Too Healthy')) {
                    const eventTypeDesc = MicrositeEventType[data.eventType];
                    if (!description.includes(eventTypeDesc)) {
                        description = description ? description : '';
                    }
                }
            }
        } else {
            const logEntry = data.logEntry || '';
            const micrositeStatus = data.micrositeRecipientStatus;

            if (data.customSiteStatus > 0 || isApprovalStatus(logEntry)) {
                const entry = logEntry.toLowerCase();
                const details = logEntry.split(':')[1]?.trim() || '';

                if (entry.includes('approved')) {
                    if (entry.includes('coach')) {
                        description = 'Approved by Coach, Pending Center Review';
                    } else if (entry.includes('center')) {
                        description = 'Approved by Center, Pending NKR Review';
                    } else if (entry.includes('nkr')) {
                        description = 'Approved by NKR, Pending Center Review';
                    }
                } else if (entry.includes('declined') || entry.includes('denied')) {
                    if (entry.includes('coach')) {
                        description = 'Denied by Coach';
                    } else if (entry.includes('center')) {
                        description = 'Denied by Center';
                    } else if (entry.includes('nkr')) {
                        description = 'Denied by NKR';
                    }
                } else {
                    const wasInactive = entry.includes('inactive') || data.previousStatus === 'Inactive';
                    const prefix = wasInactive ? 'Change Request Submitted: ' : '';

                    if (entry.includes('coach') || entry.includes('ready for coach')) {
                        description = `${prefix}Pending Coach Review`;
                    } else if (entry.includes('center') || entry.includes('ready for center') || micrositeStatus.includes('Ready for Center Review')) {
                        description = `${prefix}Pending Center Review`;
                    } else if (entry.includes('nkr') || entry.includes('ready for nkr')) {
                        description = `${prefix}Pending NKR Review`;
                    }
                }

                if (details && !description.toLowerCase().includes(details.toLowerCase())) {
                    description += ` (${details})`;
                }
            } else {
                if (micrositeStatus.includes('164')) {
                    description = 'Duplicate Entry';
                } else if (micrositeStatus.includes('Ready for Center Review')) {
                    description = 'Pending Center Review';
                } else {
                    description = formatStatusText(micrositeStatus
                        .replace(/^\d+\s*/, '') // Remove leading numbers
                        .replace('Ready for', 'Pending')
                        .trim());
                }

                if (logEntry && !description.toLowerCase().includes(logEntry.toLowerCase())) {
                    const cleanLogEntry = logEntry
                        .replace('Ready for', 'Pending')
                        .trim();
                    if (cleanLogEntry && !description.toLowerCase().includes(cleanLogEntry.toLowerCase())) {
                        description += ` (${cleanLogEntry})`;
                    }
                }
            }
        }

        if (description.trim() == '(00)') //something is wrong with the logic above when handling logs for updated events
        {
            description = data.logEntry
        }

        return description || data.logEntry || '--';
    };

    const columns: ColDef[] = [
        {
            field: 'eventDate',
            headerName: 'Event Date',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Event Date</SortableHeaderComponent>,
            valueFormatter: formatDateTime,
            comparator: Utility.dateComparator,
            sortable: true,
            flex: 1,
            cellClass: (params) => {
                if (!params.data.isEvent && (params.data.customSiteStatus > 0 || isApprovalStatus(params.data.logEntry))) {
                    return 'approval-row list-cell';
                }

                return params.data.isEvent === true ? 'event-row list-cell' : 'status-row list-cell';
            }
        },
        {
            field: 'changeType',
            headerName: 'Type',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>,
            cellRendererFramework: getTypeLabel,
            flex: 0.7,
            cellClass: (params) => {
                if (!params.data.isEvent && (params.data.customSiteStatus > 0 || isApprovalStatus(params.data.logEntry))) {
                    return 'approval-row list-cell';
                }

                return params.data.isEvent === true ? 'event-row list-cell' : 'status-row list-cell';
            }
        },
        {
            field: 'statusType',
            headerName: 'Status',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>,
            cellRendererFramework: getStatusTypeLabel,
            flex: 1,
            cellClass: (params) => {
                if (!params.data.isEvent && (params.data.customSiteStatus > 0 || isApprovalStatus(params.data.logEntry))) {
                    return 'approval-row list-cell';
                }

                return params.data.isEvent === true ? 'event-row list-cell' : 'status-row list-cell';
            }
        },
        {
            field: 'description',
            headerName: 'Description',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Description</SortableHeaderComponent>,
            cellRendererFramework: formatDescription,
            wrapText: true,
            autoHeight: true,
            flex: 3,
            cellClass: (params) => {
                if (!params.data.isEvent && (params.data.customSiteStatus > 0 || isApprovalStatus(params.data.logEntry))) {
                    return 'approval-row list-cell';
                }

                return params.data.isEvent === true ? 'event-row list-cell' : 'status-row list-cell';
            },
            tooltipField: 'logEntry'
        },
        {
            field: 'userName',
            headerName: 'Updated By',
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Updated By</SortableHeaderComponent>,
            flex: 1,
            cellClass: (params) => {
                if (!params.data.isEvent && (params.data.customSiteStatus > 0 || isApprovalStatus(params.data.logEntry))) {
                    return 'approval-row list-cell';
                }

                return params.data.isEvent === true ? 'event-row list-cell' : 'status-row list-cell';
            }
        }
    ];

    const filteredData = useMemo(() => {
        return props.logs.filter(log => {
            if (recordTypeFilter !== 'all') {
                if (recordTypeFilter === 'event' && !log.isEvent) return false;
                if (recordTypeFilter === 'status' && log.isEvent) return false;
            }

            if (statusTypeFilter !== 'all') {
                const specificStatuses = getSpecificStatusTypes(statusTypeFilter);

                if (log.isEvent) {
                    const eventType = MicrositeEventType[log.eventType];

                    if (statusTypeFilter === 'Medical Status Change') {
                        return eventType === 'TooHealthy' || eventType === 'TooSick';
                    }

                    if (statusTypeFilter === 'Center Change') {
                        return eventType?.toLowerCase().includes('moved') ||
                            log.logEntry?.toLowerCase().includes('center change');
                    }

                    if (statusTypeFilter === 'All Transplant Events') {
                        return isTransplantEvent(log.eventType);
                    }

                    return specificStatuses.some(status =>
                        formatStatusText(eventType) === status ||
                        eventType === status
                    );
                } else {
                    const logEntry = (log.logEntry || '').toLowerCase();
                    const micrositeStatus = (log.micrositeRecipientStatus || '').toLowerCase();

                    return specificStatuses.some(status => {
                        const statusLower = status.toLowerCase();
                        return micrositeStatus.includes(statusLower) ||
                            logEntry.includes(statusLower) ||
                            formatStatusText(micrositeStatus) === status;
                    });
                }
            }

            return true;
        });
    }, [props.logs, recordTypeFilter, statusTypeFilter]);

    const sortedData = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            try {
                // Default to logDateTime if eventDate is not available
                const dateA = a.eventDate ? moment(a.eventDate).valueOf() : moment(a.logDateTime).valueOf();
                const dateB = b.eventDate ? moment(b.eventDate).valueOf() : moment(b.logDateTime).valueOf();
                return dateB - dateA; // Descending order (newer dates first)
            } catch (e) {
                console.error('Date sorting error:', e);
                return 0;
            }
        });
    }, [filteredData]);

    const gridHeight = useMemo(() => {
        const rowCount = sortedData.length;

        if (rowCount === 0) return 100; // Minimum height when no data

        const calculatedHeight = rowCount * 35 + 50;

        return Math.min(500, calculatedHeight);
    }, [sortedData.length]);

    const getGroupedStatusTypes = useMemo(() => {
        const groups: { [key: string]: string[] } = {
            'Transplant Events': [],
            'Key Events': [],
            'Site Status': [],
            'Approval Workflow': [],
            'Special Status': []
        };

        if (recordTypeFilter === 'all' || recordTypeFilter === 'event') {
            groups['Transplant Events'] = [
                'All Transplant Events',
                'Living Donor Transplant',
                'Deceased Donor Transplant',
                'Living Donor Transplant (Other Center)'
            ];

            groups['Key Events'] = [
                'Invitation Sent',
                'Center Change',
                'Medical Status Change',
                'Contact Made',
                'Contact Attempted'
            ];
        }

        if (recordTypeFilter === 'all' || recordTypeFilter === 'status') {
            groups['Site Status'] = [
                'Active',
                'Inactive',
                'Deactivated'
            ];

            groups['Approval Workflow'] = [
                'Pending Review',
                'Approved',
                'Declined'
            ];

            groups['Special Status'] = [
                'Reactivated',
                'Changes Required',
                'Duplicate Entry',
                'Not Responsive'
            ];
        }

        return Object.fromEntries(
            Object.entries(groups).filter(([_, values]) => values.length > 0)
        );
    }, [recordTypeFilter]);

    const FilterControls = () => (
        <Row>
            <Col xs={6}>
                <FormGroup>
                    <ControlLabel>Log Type</ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={recordTypeFilter}
                        onChange={handleRecordTypeChange}
                    >
                        <option value="all">All Types</option>
                        <option value="event">Events</option>
                        <option value="status">Microsites</option>
                    </FormControl>
                </FormGroup>
            </Col>
            <Col xs={6}>
                <FormGroup>
                    <ControlLabel>
                        {recordTypeFilter === 'event' ? 'Event Type' :
                            recordTypeFilter === 'status' ? 'Status' :
                                'Status Type'}
                    </ControlLabel>
                    <FormControl
                        componentClass="select"
                        value={statusTypeFilter}
                        onChange={handleStatusTypeChange}
                        style={{ fontWeight: statusTypeFilter === 'all' ? 'normal' : 'bold' }}
                    >
                        <option value="all">All Types</option>
                        {Object.entries(getGroupedStatusTypes).map(([group, statuses]) => (
                            <optgroup key={group} label={group}>
                                {statuses.map(status => (
                                    <option key={status} value={status}>
                                        {status}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </FormControl>
                </FormGroup>
            </Col>
        </Row>
    );

    const getCurrentStatus = useMemo((): StatusInfo => {
        if (!props.logs || props.logs.length === 0) {
            return {
                status: 'Unknown',
                type: '',
                isActive: false,
                date: new Date(),
                timeAgo: 'N/A',
                color: '#777'
            };
        }

        const statusLogs = props.logs
            .filter(log => !log.isEvent)
            .sort((a, b) => moment(b.logDateTime).valueOf() - moment(a.logDateTime).valueOf());

        if (statusLogs.length === 0) {
            return {
                status: 'No Status',
                type: '',
                isActive: false,
                date: new Date(),
                timeAgo: 'N/A',
                color: '#777'
            };
        }

        const latestStatus = statusLogs[0];
        const status = latestStatus.micrositeRecipientStatus?.toLowerCase() || '';
        const isActive = status.includes('active');
        const date = new Date(latestStatus.logDateTime);

        let statusText = 'Unknown';
        let type = '';
        let color = '#777';

        if (status.includes('active')) {
            statusText = 'Active';
            color = '#5cb85c'; // green

            if (status.includes('starter')) {
                type = 'Starter Site';
            } else if (status.includes('custom')) {
                type = 'Custom Site';
            }
        } else if (status.includes('inactive')) {
            statusText = 'Inactive';
            color = '#777'; // gray
        } else if (status.includes('deactivate')) {
            statusText = 'Deactivated';
            color = '#d9534f'; // red
        } else if (status.includes('readyfortransplant')) {
            statusText = 'Reactivated';
            color = '#5bc0de'; // info blue
        } else if (status.includes('pending')) {
            statusText = 'Pending Review';
            color = '#f0ad4e'; // warning yellow
        }

        return {
            status: statusText,
            type,
            isActive,
            date,
            timeAgo: moment(date).fromNow(),
            color
        };
    }, [props.logs]);

    const getInvitationInfo = useMemo((): InvitationInfo => {
        if (!props.logs || props.logs.length === 0) {
            return {
                date: null,
                timeAgo: 'N/A',
                count: 0,
                hasInvitation: false
            };
        }

        const invitationLogs = props.logs
            .filter(log => {
                if (log.logEntry && log.logEntry.toLowerCase().includes('invitation')) {
                    return true;
                }

                if (log.isEvent && log.eventType !== undefined) {
                    const eventType = MicrositeEventType[log.eventType];
                    return eventType && eventType.toLowerCase().includes('invitation');
                }
                return false;
            })
            .sort((a, b) => moment(b.logDateTime).valueOf() - moment(a.logDateTime).valueOf());

        const count = invitationLogs.length;

        if (count === 0) {
            return {
                date: null,
                timeAgo: 'Never',
                count: 0,
                hasInvitation: false
            };
        }

        const latestInvitation = invitationLogs[0];
        const date = new Date(latestInvitation.logDateTime);

        return {
            date,
            timeAgo: moment(date).fromNow(),
            count,
            hasInvitation: true
        };
    }, [props.logs]);

    // const getTransplantInfo = useMemo((): TransplantInfo => {
    //     if (!props.logs || props.logs.length === 0) {
    //         return {
    //             hasTransplant: false,
    //             count: 0,
    //             lastTransplantDate: null,
    //             timeAgo: 'N/A',
    //             status: 'No Transplant'
    //         };
    //     }

    //     // Find all transplant-related events - only look for specific enum values
    //     const transplantLogs = props.logs
    //         .filter(log => log.isEvent && isTransplantEvent(log.eventType))
    //         .sort((a, b) => moment(b.logDateTime).valueOf() - moment(a.logDateTime).valueOf());

    //     const count = transplantLogs.length;

    //     if (count === 0) {
    //         return {
    //             hasTransplant: false,
    //             count: 0,
    //             lastTransplantDate: null,
    //             timeAgo: 'N/A',
    //             status: 'No Transplant'
    //         };
    //     }

    //     const latestTransplant = transplantLogs[0];
    //     const date = new Date(latestTransplant.logDateTime);

    //     return {
    //         hasTransplant: true,
    //         count,
    //         lastTransplantDate: date,
    //         timeAgo: moment(date).fromNow(),
    //         status: getTransplantTypeDescription(latestTransplant.eventType)
    //     };
    // }, [props.logs]);

    const getActivityInfo = useMemo((): ActivityInfo => {
        if (!props.logs || props.logs.length === 0) {
            return {
                totalEvents: 0,
                statusUpdates: 0,
                events: 0,
                lastChange: {
                    date: null,
                    description: 'No activity',
                    timeAgo: 'N/A'
                }
            };
        }

        const events = props.logs.filter(log => log.isEvent).length;
        const statusUpdates = props.logs.length - events;

        // Get the most recent significant change
        const sortedLogs = [...props.logs].sort((a, b) =>
            moment(b.logDateTime).valueOf() - moment(a.logDateTime).valueOf()
        );

        const latestLog = sortedLogs[0];
        const date = new Date(latestLog.logDateTime);
        let description = '';

        if (latestLog.isEvent) {
            if (latestLog.eventType !== undefined && MicrositeEventType[latestLog.eventType]) {
                description = `Event: ${formatStatusText(MicrositeEventType[latestLog.eventType])}`;
            } else {
                description = 'Event occurred';
            }
        } else {
            const status = latestLog.micrositeRecipientStatus?.replace(/^\d+\s*/, '').trim() || '';
            description = `Status changed to: ${formatStatusText(status)}`;
        }

        return {
            totalEvents: props.logs.length,
            statusUpdates,
            events,
            lastChange: {
                date,
                description,
                timeAgo: moment(date).fromNow()
            }
        };
    }, [props.logs]);

    const showStatusUpdatesOnly = () => {
        if (recordTypeFilter !== 'status' || statusTypeFilter !== 'all') {
            setRecordTypeFilter('status');
            setStatusTypeFilter('all');
        }
    };

    const showInvitationsOnly = () => {
        if (recordTypeFilter !== 'status' || statusTypeFilter !== 'Invitation Sent') {
            setRecordTypeFilter('status');
            setStatusTypeFilter('Invitation Sent');
        }
    };

    const showTransplantEvents = () => {
        if (recordTypeFilter !== 'event' || statusTypeFilter !== 'All Transplant Events') {
            setRecordTypeFilter('event');
            setStatusTypeFilter('All Transplant Events');
        }
    };

    const showAllRecords = () => {
        if (recordTypeFilter !== 'all' || statusTypeFilter !== 'all') {
            setRecordTypeFilter('all');
            setStatusTypeFilter('all');
        }
    };

    const getAllTransplantEvents = useMemo(() => {
        return props.logs
            .filter(log => log.isEvent && isTransplantEvent(log.eventType))
            .sort((a, b) => moment(b.logDateTime).valueOf() - moment(a.logDateTime).valueOf())
            .map(event => ({
                date: moment(event.logDateTime),
                type: getTransplantTypeDescription(event.eventType),
                formattedDate: moment(event.logDateTime).format('MMM D, YYYY')
            }));
    }, [props.logs]);

    const SummaryPanel = () => {
        if (!showSummary) {
            return (
                <SummaryHeader>
                    <button type="button" className="toggle-btn" onClick={() => setShowSummary(true)}>
                        <Glyphicon glyph="chevron-down" /> Show Summary
                    </button>
                </SummaryHeader>
            );
        }

        const formattedStatusDate = getCurrentStatus.date ? moment(getCurrentStatus.date).format('MMM D, YYYY') : null;

        const formattedInvitationDate = getInvitationInfo.date ? moment(getInvitationInfo.date).format('MMM D, YYYY') : null;

        return (
            <SummaryContainer>
                <SummaryCardContainer>
                    <SummaryCard bsStyle="default">
                        <div className="panel-heading">
                            Activity Overview
                            <Badge>{getActivityInfo.totalEvents}</Badge>
                        </div>
                        <div className="panel-body">
                            <div className="summary-value">
                                {getActivityInfo.events} Events, {getActivityInfo.statusUpdates} Status Updates
                            </div>
                            <div className="summary-subtext">
                                Last Activity: <span className="summary-date">{moment(getActivityInfo.lastChange.date).fromNow()}</span>
                            </div>
                            <div className="summary-subtext" style={{ marginTop: '5px' }}>
                                {getActivityInfo.lastChange.description}
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-default"
                                    onClick={showAllRecords}
                                >
                                    <Glyphicon glyph="list" /> Show All Records
                                </button>
                            </div>
                        </div>
                    </SummaryCard>

                    {/* Microsite Status Card (formerly Current Status) */}
                    <SummaryCard bsStyle="default">
                        <div className="panel-heading">
                            Microsite Status
                            <StatusBadge color={getCurrentStatus.color}>{getCurrentStatus.status}</StatusBadge>
                        </div>
                        <div className="panel-body">
                            <div className="summary-value">
                                {getCurrentStatus.type ? `${getCurrentStatus.type}` : getCurrentStatus.status}
                            </div>
                            <div className="summary-subtext">
                                {getCurrentStatus.status === 'Active' ? 'Active since' : 'Last updated'}: <span className="summary-date">{formattedStatusDate}</span>
                            </div>
                            <div className="summary-subtext">
                                {moment(getCurrentStatus.date).fromNow()}
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-primary"
                                    onClick={showStatusUpdatesOnly}
                                >
                                    <Glyphicon glyph="filter" /> View History
                                </button>
                            </div>
                        </div>
                    </SummaryCard>

                    {/* Transplant & Voucher Overview Card */}
                    <SummaryCard bsStyle="default">
                        <div className="panel-heading">
                            Transplant & Voucher Activity
                            <Badge>{getAllTransplantEvents.length || 0}</Badge>
                        </div>
                        <div className="panel-body">
                            {getAllTransplantEvents.length > 0 ? (
                                <>
                                    <div className="summary-value">
                                        {getAllTransplantEvents[0].type}
                                    </div>
                                    <div className="summary-subtext">
                                        Date: <span className="summary-date">{getAllTransplantEvents[0].formattedDate}</span>
                                    </div>
                                    {getAllTransplantEvents.length > 1 && (
                                        <div className="summary-subtext">
                                            Total Events: {getAllTransplantEvents.length}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="summary-value">
                                        No Activity
                                    </div>
                                    <div className="summary-subtext">
                                        No transplant or voucher events recorded
                                    </div>
                                </>
                            )}

                            {/* Buttons */}
                            <div style={{ marginTop: '10px', display: 'flex', gap: '5px' }}>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-primary"
                                    onClick={showTransplantEvents}
                                >
                                    <Glyphicon glyph="filter" /> View Details
                                </button>
                            </div>
                        </div>
                    </SummaryCard>

                    {/* Invitation Card */}
                    <SummaryCard bsStyle="default">
                        <div className="panel-heading">
                            Communications
                            {getInvitationInfo.hasInvitation &&
                                <Badge>{getInvitationInfo.count}</Badge>
                            }
                        </div>
                        <div className="panel-body">
                            <div className="summary-value">
                                {getInvitationInfo.hasInvitation ? 'Last Sent' : 'No Invitations Sent'}
                            </div>
                            {getInvitationInfo.hasInvitation ? (
                                <>
                                    <div className="summary-subtext">
                                        Sent on: <span className="summary-date">{formattedInvitationDate}</span>
                                    </div>
                                    <div className="summary-subtext">
                                        Total Sent: {getInvitationInfo.count}
                                    </div>
                                </>
                            ) : (
                                <div className="summary-subtext">
                                    No invitations have been sent to this recipient
                                </div>
                            )}
                            <div style={{ marginTop: '10px' }}>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-primary"
                                    onClick={showInvitationsOnly}
                                    disabled={!getInvitationInfo.hasInvitation}
                                >
                                    <Glyphicon glyph="filter" /> View Invitations
                                </button>
                            </div>
                        </div>
                    </SummaryCard>
                </SummaryCardContainer>
            </SummaryContainer>
        );
    };

    return (
        <div style={{ marginBottom: '20px' }}>
            <div>
                <SummaryPanel />
                <FilterContainer>
                    <FilterControls />
                </FilterContainer>
                <HistoryGridWrapper
                    id='grid-wrapper'
                    className='ag-theme-bootstrap'
                    style={{ height: gridHeight }}
                >
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                            resizable: false,
                            cellStyle: {
                                display: 'flex',
                                alignItems: 'center',
                            }
                        }}
                        columnDefs={columns}
                        rowData={sortedData}
                        headerHeight={30}
                        gridOptions={{
                            multiSortKey: 'ctrl'
                        }}
                        autoGroupColumnDef={null}
                        suppressAutoSize={true}
                        suppressContextMenu={true}
                        domLayout='autoHeight'
                    />
                </HistoryGridWrapper>
            </div>
        </div>
    );
}

export default MicrositeHistory;